import type { IERecruiterOffer, IOffer, IQuery } from '@/types/shared-types'
import adaptOffer from '@/composables/adapters/adapt-offer'
import useFetchWithCache from '@/composables/useFetchWithCache'
import { AREAS, defaultCategory } from '~/domains/career/components/career-searchBar/career-searchBar.consts'
import type { IOption } from '~/domains/career/components/career-searchBar/career-searchBar.types'

const ERYK_URL = 'https://offers.erecruiter.pl/skk/company/18799808/offers.json?hash=C8485656BB98BDA33534B16728E9C1526AC5E90CE670588426B3D5F007DE890B5C24F55F465FA7A5EF4F8FF371B03E2848F5BF8342B5A7678FFD75F292165F9A'
const categoriesMap: Record<string, string> = {
  technologie: 'technology',
  obsluga: 'service',
  sprzedaz: 'sell',
  pozostale: 'others',
  'praktyki-i-staze': 'interships',
}

export default function useErecruiter() {
  const getData = async (): Promise<{ jobs: IERecruiterOffer[] }> => {
    try {
      const data = await useFetchWithCache<{ jobs: IERecruiterOffer[] }>(ERYK_URL)
      return data
    } catch (e) {
      console.error(e)
      return { jobs: [] }
    }
  }

  const filterOfferByText = (job: IERecruiterOffer, phrase: string) => phrase ? job.title.toLowerCase().includes(phrase.toLowerCase()) : job

  const filterOfferByArea = (job: IERecruiterOffer, areas: string[]) => {
    if (areas.length) {
      const hasOthersFilter = areas.some(key => AREAS.find(a => a.label === key && a.key === 'others'))
      return (hasOthersFilter && !AREAS.some(ar => job.additionalFields[ar.label])) || Object.keys(job.additionalFields).some(key => areas.includes(key))
    }

    return job
  }

  const filterOfferByCity = (job: IERecruiterOffer, cities: string[]) => {
    if (cities.length) {
      return cities.includes(job.location)
    }

    return job
  }

  const getOffersWithOtherArea = (job: IERecruiterOffer, area: IOption) => !AREAS.some(ar => job.additionalFields[ar.label]) || job.additionalFields[area.label]

  const sortByDate = (a: IOffer, b: IOffer) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()

  const getOffers = async (query?: IQuery): Promise<IOffer[]> => {
    const data = await getData()

    if (query && Object.keys(query).length && Object.keys(query).some(key => query[key].length)) {
      return data.jobs
        .filter(job => filterOfferByText(job, query.position as string))
        .filter(job => filterOfferByText(job, query.keywords as string))
        .filter(job => filterOfferByArea(job, query.areas as string[]))
        .filter(job => filterOfferByCity(job, query.cities as string[]))
        .map(job => adaptOffer(job))
        .sort(sortByDate)
    }

    return data.jobs.map(job => adaptOffer(job)).sort(sortByDate)
  }

  const getOffersByCategory = async (category: string): Promise<IOffer[] | Record<string, IOffer[]>> => {
    const data = await getData()
    if (category === defaultCategory) {
      const offers: Record<string, IOffer[]> = {}
      AREAS.forEach((a) => {
        data.jobs.forEach((j) => {
          // Some of the offers doesn't have attributes in additionalFields record, so we need to put them with Others
          if (j.additionalFields[a.label] || (!AREAS.some(ar => j.additionalFields[ar.label]) && a.key === 'others')) {
            if (offers[a.label]) {
              offers[a.label].push(adaptOffer(j))
            } else {
              offers[a.label] = [adaptOffer(j)]
            }
          }
        })

        if (offers[a.label]) {
          offers[a.label] = offers[a.label].sort(sortByDate)
        }
      })
      
      return offers
    }

    const area = AREAS.find(a => a.key === categoriesMap[category])
    if (area) {
      if (area?.key === 'others') {
        // Some of the offers doesn't have attributes in additionalFields record, so we need to put them with Others
        return data.jobs.filter(job => getOffersWithOtherArea(job, area))
          .map(job => adaptOffer(job))
          .sort(sortByDate)
      }

      return data.jobs.filter((job) => {
        return job.additionalFields[area.label]
      })
        .map(job => adaptOffer(job))
        .sort(sortByDate)
    }

    return data.jobs.map(job => adaptOffer(job)).sort(sortByDate)
  }

  const getLastOffers = async (): Promise<IOffer[]> => {
    const data = await getData()
    return data.jobs.map(job => adaptOffer(job))
      .sort(sortByDate)
      .slice(0, 6)
  }

  return {
    getOffers,
    getLastOffers,
    getOffersByCategory,
  }
}
